import { ref, watch, nextTick, onUnmounted, type Ref } from 'vue';
import { onClickOutside } from '@vueuse/core';

export interface AnchoredDropdownOptions {
  minWidth?: number;
  maxWidth?: number;
  gap?: number;
  /** When false, clicking outside does not auto-close. Default: true. */
  closeOnOutsideClick?: boolean;
}

/**
 * Positions a teleported dropdown/popover anchored to a trigger element.
 *
 * The popup is rendered via `<Teleport to="body">` in the caller's template, so
 * it escapes overflow/clipping in bars and panels.  This composable computes the
 * `position: fixed` style to align the popup with the trigger, flipping above or
 * below based on available viewport space.
 *
 * @param triggerRef  Ref to the element that opens the dropdown (button/span).
 * @param dropdownRef Ref to the dropdown/popover root element (inside Teleport).
 * @param options     Optional sizing, gap, and outside-click configuration.
 */
export function useAnchoredDropdown(
  triggerRef: Ref<HTMLElement | null>,
  dropdownRef: Ref<HTMLElement | null>,
  options: AnchoredDropdownOptions = {},
) {
  const { minWidth = 220, maxWidth = 340, gap = 4, closeOnOutsideClick = true } = options;
  const isOpen = ref(false);
  const dropdownStyle = ref<Record<string, string>>({});

  function computePosition() {
    const trigger = triggerRef.value;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vp = 8; // minimum distance from viewport edge
    const width = Math.min(maxWidth, Math.max(minWidth, window.innerWidth - vp * 2));
    const left = Math.min(
      Math.max(vp, rect.left),
      window.innerWidth - width - vp,
    );
    const spaceAbove = rect.top - vp;
    const spaceBelow = window.innerHeight - rect.bottom - vp;

    // Build new style object
    let next: Record<string, string>;
    if (spaceAbove > spaceBelow) {
      next = {
        position: 'fixed',
        bottom: `${window.innerHeight - rect.top + gap}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(0, spaceAbove)}px`,
        zIndex: '1500',
      };
    } else {
      next = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(0, spaceBelow)}px`,
        zIndex: '1500',
      };
    }

    // Skip Vue reactivity update when nothing changed (prevents no-op re-renders on scroll)
    const prev = dropdownStyle.value;
    for (const k in next) {
      if (prev[k] !== next[k]) { dropdownStyle.value = next; return; }
    }
    for (const k in prev) {
      if (!(k in next)) { dropdownStyle.value = next; return; }
    }
  }

  // Throttle: at most one reposition per animation frame to avoid layout thrashing on rapid scroll
  let rafId: number | null = null;
  function syncPosition() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      computePosition();
    });
  }

  async function open() {
    isOpen.value = true;
    await nextTick(); // wait for Teleport to render the dropdown before measuring
    computePosition();
  }

  function close() {
    isOpen.value = false;
  }

  function toggle() {
    if (isOpen.value) close(); else open();
  }

  watch(isOpen, (v) => {
    if (v) {
      window.addEventListener('resize', syncPosition);
      window.addEventListener('scroll', syncPosition, true);
    } else {
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
      window.removeEventListener('resize', syncPosition);
      window.removeEventListener('scroll', syncPosition, true);
    }
  });

  if (closeOnOutsideClick) {
    onClickOutside(dropdownRef, close, { ignore: [triggerRef] });
  }

  onUnmounted(() => {
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    window.removeEventListener('resize', syncPosition);
    window.removeEventListener('scroll', syncPosition, true);
  });

  return {
 isOpen,
dropdownStyle,
open,
close,
toggle,
};
}
